import React, { useState } from 'react';
import { Image, RefreshCw, Check, Edit3, Sparkles } from 'lucide-react';

interface RecipeImagePreviewProps {
  imageBase64: string;
  recipeName: string;
  recipeDescription: string;
  onConfirm: (finalImageBase64: string) => void;
  onSkip: () => void;
  backendUrl: string;
  geminiApiKey: string;
}

export const RecipeImagePreview: React.FC<RecipeImagePreviewProps> = ({
  imageBase64,
  recipeName,
  recipeDescription,
  onConfirm,
  onSkip,
  backendUrl,
}) => {
  const [currentImage, setCurrentImage] = useState(imageBase64);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [showCustomPrompt, setShowCustomPrompt] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');
  const [lastUsedPrompt, setLastUsedPrompt] = useState(''); // Stocker le dernier prompt utilisé
  const [error, setError] = useState<string | null>(null);

  const handleRegenerate = async (useCustomPrompt: boolean = false) => {
    setIsRegenerating(true);
    setError(null);

    try {
      const promptToUse = useCustomPrompt ? customPrompt : null;
      
      const response = await fetch(`${backendUrl}/api/regenerate-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipeName,
          description: recipeDescription,
          customPrompt: promptToUse,
        }),
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la régénération');
      }

      const result = await response.json();
      setCurrentImage(result.data.generatedImage);
      
      // Sauvegarder le prompt utilisé
      if (useCustomPrompt && customPrompt) {
        setLastUsedPrompt(customPrompt);
      }
      
      setShowCustomPrompt(false);
      setCustomPrompt('');
    } catch (err) {
      console.error('Erreur:', err);
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleConfirm = () => {
    onConfirm(currentImage);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-2xl font-bold text-gray-900 flex items-center justify-center gap-2">
          <Sparkles className="h-6 w-6 text-purple-600" />
          Aperçu de l'image générée
        </h3>
        <p className="text-gray-600 mt-2">
          Validez l'image ou régénérez-en une nouvelle
        </p>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Image Preview */}
      <div className="bg-white rounded-lg p-6 border-2 border-gray-200 shadow-sm">
        <div className="relative aspect-square max-w-2xl mx-auto rounded-lg overflow-hidden bg-gray-100">
          {isRegenerating ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mb-4"></div>
              <p className="text-gray-600 font-medium">Génération en cours...</p>
              <p className="text-sm text-gray-500 mt-2">Cela peut prendre quelques secondes</p>
            </div>
          ) : (
            <img
              src={`data:image/jpeg;base64,${currentImage}`}
              alt={recipeName}
              className="w-full h-full object-cover"
            />
          )}
        </div>

        {/* Recipe Title */}
        <div className="mt-4 text-center">
          <h4 className="text-xl font-bold text-gray-900">{recipeName}</h4>
          {recipeDescription && (
            <p className="text-gray-600 mt-1 text-sm">{recipeDescription}</p>
          )}
        </div>
      </div>

      {/* Custom Prompt Section */}
      {showCustomPrompt && (
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Edit3 className="inline h-4 w-4 mr-1" />
            Décrivez l'image que vous souhaitez
          </label>
          <textarea
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
            placeholder="Ex: Une photo en gros plan avec beaucoup de fromage fondu, éclairage chaud..."
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled={isRegenerating}
          />
          <div className="flex gap-2 mt-3">
            <button
              onClick={() => handleRegenerate(true)}
              disabled={isRegenerating || !customPrompt.trim()}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Sparkles className="h-4 w-4" />
              Générer avec ce prompt
            </button>
            <button
              onClick={() => {
                setShowCustomPrompt(false);
                setCustomPrompt('');
              }}
              disabled={isRegenerating}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-3 justify-center">
        {!showCustomPrompt && (
          <>
            <button
              onClick={() => handleRegenerate(false)}
              disabled={isRegenerating}
              className="flex items-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw className={`h-5 w-5 ${isRegenerating ? 'animate-spin' : ''}`} />
              Régénérer
            </button>

            <button
              onClick={() => {
                setShowCustomPrompt(true);
                // Pré-remplir avec le dernier prompt utilisé ou le prompt par défaut
                if (lastUsedPrompt) {
                  setCustomPrompt(lastUsedPrompt);
                } else {
                  setCustomPrompt(`Image de ${recipeName}${recipeDescription ? `\n${recipeDescription}` : ''}\nsans texte`);
                }
              }}
              disabled={isRegenerating}
              className="flex items-center gap-2 px-6 py-3 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Edit3 className="h-5 w-5" />
              Modifier le prompt
            </button>
          </>
        )}

        <button
          onClick={onSkip}
          disabled={isRegenerating}
          className="flex items-center gap-2 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Image className="h-5 w-5" />
          Continuer sans image
        </button>

        <button
          onClick={handleConfirm}
          disabled={isRegenerating}
          className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-medium rounded-lg hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md"
        >
          <Check className="h-5 w-5" />
          Valider cette image
        </button>
      </div>

      {/* Tips */}
      <div className="text-center text-sm text-gray-500 space-y-1">
        <p>💡 <strong>Astuce :</strong> Cliquez sur "Régénérer" pour obtenir une variation</p>
        <p>✏️ Utilisez "Modifier le prompt" pour un contrôle total sur l'image</p>
      </div>
    </div>
  );
};

